import { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import GoogleIcon from "@mui/icons-material/Google";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { api } from "../api/client";
import type { GmailSenderSettings } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import {
  ConfirmDialog,
  ErrorBanner,
  LoadingState,
  Mono,
  PageHeader,
  RoleGate,
  when,
} from "../components/common";
import { gmailReturnMessage, gmailSenderState } from "../components/EmailChannelForm";

/**
 * Server-wide configuration. Today that is one thing: the Google account this
 * deployment sends lead emails from.
 *
 * It has its own screen because it is not a property of any client. Every
 * `gmail_oauth` delivery channel mails through this one mailbox — connect it once,
 * as an owner, before or after any client exists. It previously lived inside the
 * "add a delivery channel" dialog, which made the company's outbound identity
 * something you could only reach by pretending to configure a customer, and made
 * the same consent screen a per-client chore.
 *
 * The screen is readable by anyone who can read the console: knowing which mailbox
 * leads come from is exactly what an operator needs to explain a delivery, and an
 * address is not a credential. Connecting and disconnecting are owner-only, both
 * here and in the API — this decides the From address on mail sent to every
 * client's leads, and a disconnect stops all of it at once.
 */
export function SettingsPage() {
  const { can } = useAuth();
  const settings = useResource<GmailSenderSettings>("/api/settings/gmail");

  // Where the Google OAuth callback lands. It carries one word saying how the
  // consent went; anything else in the query string is ignored. The word is latched
  // into state and then stripped from the URL, so the banner survives but a reload
  // or a Back does not re-announce a connection that happened ten minutes ago.
  const [search, setSearch] = useSearchParams();
  const [oauthReturn, setOauthReturn] = useState(() => gmailReturnMessage(search.get("gmail")));
  useEffect(() => {
    const outcome = search.get("gmail");
    if (outcome === null) return;
    setOauthReturn(gmailReturnMessage(outcome));
    if (outcome === "connected") settings.reload();
    const next = new URLSearchParams(search);
    next.delete("gmail");
    setSearch(next, { replace: true });
    // `settings.reload` is deliberately not a dependency: it changes identity on
    // every fetch, and the effect is already idempotent — the parameter is gone
    // after the first run, so every later run returns above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, setSearch]);

  const [toast, setToast] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  /**
   * Hand the tab to Google.
   *
   * A full navigation, not a fetch: the consent screen is a page on Google, and a
   * 302 followed by `fetch` would land the HTML in a promise instead of the tab.
   */
  const connect = useMutation(async () => {
    const { authorizationUrl } = await api.post<{ authorizationUrl: string }>(
      "/api/settings/gmail/connect",
    );
    window.location.assign(authorizationUrl);
    // Nothing after this matters — the tab is leaving — but the promise has to stay
    // pending-looking rather than resolve into a UI update mid-navigation.
    await new Promise(() => {});
  });

  const disconnect = useMutation(async () => {
    await api.post("/api/settings/gmail/disconnect");
  });

  if (settings.loading && !settings.data) return <LoadingState label="Loading settings…" />;
  if (settings.error) return <ErrorBanner error={settings.error} onRetry={settings.reload} />;

  const state = gmailSenderState(settings.data);
  const connection = settings.data?.connection;
  const channels = settings.data?.channels ?? { total: 0, active: 0 };
  const connected = state.kind === "connected";
  const available = state.kind !== "unavailable";
  const busy = connect.pending || disconnect.pending;

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Configuration that belongs to this deployment rather than to any one client."
      />

      {oauthReturn ? (
        <Alert severity={oauthReturn.severity} sx={{ mb: 2 }} onClose={() => setOauthReturn(null)}>
          {oauthReturn.message}
        </Alert>
      ) : null}

      <ErrorBanner error={connect.error ?? disconnect.error} />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Email sender</Typography>
            <Typography variant="body2" color="text.secondary">
              The single Google account this server sends lead emails as. Every Gmail delivery
              channel uses it; SMTP channels bring their own relay and are unaffected.
            </Typography>
          </Box>

          {state.kind === "unavailable" ? (
            <Alert severity="warning">
              <AlertTitle>{state.headline}</AlertTitle>
              {state.detail}
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={connected ? "connected" : state.kind === "pending" ? "connecting" : "not connected"}
                    color={connected ? "success" : state.kind === "pending" ? "info" : "warning"}
                    variant="outlined"
                  />
                  <Typography variant="subtitle2">{state.headline}</Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {state.detail}
                </Typography>

                {connected && connection ? (
                  <Typography variant="caption" color="text.secondary">
                    Connected {when(connection.connectedAt)}
                    {connection.connectedBy ? ` by ${connection.connectedBy}` : ""}
                  </Typography>
                ) : null}

                <Divider />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <RoleGate allowed={can("owner")} needs="owner">
                    <Button
                      variant={connected ? "outlined" : "contained"}
                      startIcon={<GoogleIcon />}
                      disabled={busy}
                      onClick={() => void connect.run(undefined)}
                    >
                      {connect.pending
                        ? "Opening Google…"
                        : connected
                          ? "Reconnect a Google account"
                          : "Connect a Google account"}
                    </Button>
                  </RoleGate>
                  {connected ? (
                    <RoleGate allowed={can("owner")} needs="owner">
                      <Button
                        color="error"
                        startIcon={<LinkOffIcon />}
                        disabled={busy}
                        onClick={() => setDisconnecting(true)}
                      >
                        Disconnect
                      </Button>
                    </RoleGate>
                  ) : null}
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Connecting opens Google's consent screen. Only “send email on your behalf”
                  (<Mono>gmail.send</Mono>) is requested; this server never gains the ability to
                  read the account's mail. The authorization is stored encrypted and is never
                  returned to the browser.
                </Typography>
              </Stack>
            </Paper>
          )}

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              {channels.total === 0
                ? "No delivery channel uses Gmail yet."
                : `${channels.total} Gmail delivery channel${channels.total === 1 ? "" : "s"} ` +
                  `(${channels.active} active) send through this account.`}
            </Typography>
            <Button component={RouterLink} to="/clients" size="small">
              Delivery channels live on each client
            </Button>
          </Stack>

          {available && !connected && channels.active > 0 ? (
            <Alert severity="error">
              {channels.active} active Gmail channel{channels.active === 1 ? " is" : "s are"} waiting
              on a sender. Their leads are recorded as failed deliveries and retried on the next run,
              so connecting an account is enough to recover them — nothing is lost in the meantime.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <ConfirmDialog
        open={disconnecting}
        title="Disconnect the email sender?"
        confirmLabel="Disconnect"
        pending={disconnect.pending}
        body={
          <>
            Gmail delivery stops for every client immediately —{" "}
            {channels.active} active channel{channels.active === 1 ? "" : "s"} of{" "}
            {channels.total} total. The stored authorization is deleted here and revoked at Google,
            so reconnecting means granting access again. Channels are left switched on, so
            connecting a new account resumes them without touching each client.
          </>
        }
        onCancel={() => setDisconnecting(false)}
        onConfirm={() => {
          void disconnect.run(undefined).then((ok) => {
            if (!ok) return;
            setDisconnecting(false);
            setToast("Email sender disconnected");
            settings.reload();
          });
        }}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
      />
    </Box>
  );
}
